import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Form, InputGroup, Row, Spinner, Table } from "react-bootstrap";
import { useOutletContext } from "react-router-dom";
import { FiGlobe, FiLink, FiPlus, FiSearch, FiTag, FiUser, FiUsers } from "react-icons/fi";
import Header from "../components/Header";
import ClientModal from "../components/ClientModal";
import { AccessPill, ClientAvatar } from "../components/clientUi";
import { getHost } from "../utils/utils";
import "../components/MonitorsTable.css";
import "../components/AdminClients.css";

const API_CLIENTS = `${getHost()}/api/admin/clients`;
const getToken = () => localStorage.getItem("jwt");

const AdminClients = () => {
  const { toggleSidebar } = useOutletContext();
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [monitors, setMonitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(() => {
    const headers = { Authorization: `Bearer ${getToken()}` };
    return Promise.all([
      fetch(API_CLIENTS, { headers }).then((r) => r.json()),
      fetch(`${API_CLIENTS}/monitors`, { headers }).then((r) => r.json()),
    ])
      .then(([c, m]) => {
        setClients(c.clients || []);
        setCategories(c.categories || []);
        setMonitors(m.monitors || []);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const monitorById = useMemo(() => new Map(monitors.map((m) => [m._id, m])), [monitors]);

  const filtered = clients.filter((c) => {
    const q = search.trim().toLowerCase();
    return !q || c.name.toLowerCase().includes(q) || c.user_ids.some((u) => u.includes(q));
  });

  const open = (client) => {
    setEditing(client);
    setShowModal(true);
  };

  const siteSummary = (ids) => {
    const names = ids.map((id) => monitorById.get(id)?.name || id);
    return { shown: names.slice(0, 2).join(", "), more: names.length - 2, all: names.join(", ") };
  };

  return (
    <div className="w-100 monitor-setting">
      <Header toggleSidebar={toggleSidebar} />
      <div className="pb-4 mx-4 content-body">
        <Row className="gx-0 align-items-center">
          <Col md={12}>
            <div className="mb-2">
              <div className="d-inline-flex align-items-center justify-content-center bg-dark rounded p-2 mb-2">
                <FiUsers className="text-white" />
              </div>
              <h4 className="fs-22 text-main mb-1">Clients</h4>
              <p className="text-white-65 fs-14 mb-0">
                Create clients and choose what they can see: whole categories or single sites.
              </p>
            </div>
          </Col>
        </Row>
      </div>

      <div className="px-4 pb-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div className="d-flex align-items-center gap-2">
            <FiUsers className="text-gray fs-20" />
            <span className="text-white fw-500">
              {clients.length} Client{clients.length !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="monitors-toolbar d-flex flex-wrap flex-md-nowrap gap-2">
            <InputGroup className="monitors-search">
              <InputGroup.Text className="bg-dark border-secondary">
                <FiSearch className="text-gray" />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search name or Discord id..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="form-input-field border-secondary"
              />
            </InputGroup>
            <Button
              variant="dark"
              className="d-inline-flex align-items-center gap-2 border-secondary text-nowrap"
              style={{ height: "38px" }}
              onClick={() => open(null)}
            >
              <FiPlus className="fs-14" />
              <span className="fs-14">New client</span>
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" variant="light" />
          </div>
        ) : (
          <div className="table-mobile-scroll">
            <div className="table-responsive">
              <Table className="table custom-table table-bordered clients-table" hover>
                <thead className="rounded-3">
                  <tr>
                    <th className="fs-13 fw-500 border-bottom" style={{ width: "30%" }}>
                      <span className="d-inline-flex align-items-center gap-2"><FiUser /> Client</span>
                    </th>
                    <th className="fs-13 fw-500 border-bottom" style={{ width: "30%" }}>
                      <span className="d-inline-flex align-items-center gap-2"><FiTag /> Categories</span>
                    </th>
                    <th className="fs-13 fw-500 border-bottom" style={{ width: "22%" }}>
                      <span className="d-inline-flex align-items-center gap-2"><FiGlobe /> Sites</span>
                    </th>
                    <th className="fs-13 fw-500 border-bottom text-end" style={{ width: "9%" }}>
                      <span className="d-inline-flex align-items-center gap-2"><FiUsers /> Users</span>
                    </th>
                    <th className="fs-13 fw-500 border-bottom text-end" style={{ width: "9%" }}>
                      <span className="d-inline-flex align-items-center gap-2"><FiLink /> Webhooks</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center text-white-65 py-5">No clients found</td>
                    </tr>
                  )}
                  {filtered.map((c) => {
                    const sites = siteSummary(c.monitors);
                    return (
                      <tr key={c._id} onClick={() => open(c)}>
                        <td className="align-middle">
                          <div className="d-flex align-items-center gap-2">
                            <ClientAvatar src={c.avatar} name={c.name} color={c.color} />
                            <span className="fw-500 fs-14 text-main text-truncate">{c.name}</span>
                          </div>
                        </td>
                        <td className="align-middle">
                          <div className="d-flex flex-wrap gap-1">
                            {c.category.length ? (
                              c.category.map((cat) => <AccessPill key={cat} category={cat} />)
                            ) : (
                              <span className="fs-13">—</span>
                            )}
                          </div>
                        </td>
                        <td className="align-middle fs-13" title={sites.all}>
                          {c.monitors.length ? (
                            <span className="text-main">
                              {sites.shown}
                              {sites.more > 0 && <span className="text-white-65"> +{sites.more}</span>}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="align-middle text-end fs-14">{c.user_ids.length}</td>
                        <td className="align-middle text-end fs-14">{c.webhooks}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </div>
        )}
      </div>

      <ClientModal
        show={showModal}
        onHide={() => setShowModal(false)}
        client={editing}
        categories={categories}
        monitors={monitors}
        onSaved={() => {
          setShowModal(false);
          load();
        }}
      />
    </div>
  );
};

export default AdminClients;
